"use client";

import { useTranslations } from "next-intl";

export default function Error({ reset }: { reset: () => void }) {
  // Root-scoped so this boundary can reuse common.error and notFound.toHome
  // instead of duplicating them, and reads the intl context once.
  //
  // Safe in an error boundary: Next.js does not route a layout's own failure
  // to that segment's error.tsx, so whenever this renders, [locale]/layout.tsx
  // has already mounted NextIntlClientProvider around it. A missing key cannot
  // throw either - i18n/request.ts answers it through getMessageFallback (the
  // English string, else "") and swallows MISSING_MESSAGE in onError.
  const t = useTranslations();
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <p className="text-[11px] font-medium tracking-[0.12em] uppercase text-text-tertiary mb-4">{t("common.error")}</p>
        <h1 className="font-[family-name:var(--font-cormorant)] text-[36px] font-light text-foreground leading-tight mb-4">
          {t("error.title")}
        </h1>
        <p className="text-[15px] text-text-secondary leading-relaxed mb-8">
          {t("error.body")}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={reset}
            className="inline-flex items-center justify-center h-12 px-8 bg-foreground text-background text-[14px] font-semibold tracking-[0.08em] uppercase hover:bg-foreground/85 transition-colors rounded-lg"
            style={{ transitionDuration: "var(--duration-fast)" }}
          >
            {t("error.retry")}
          </button>
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- intentional full-document navigation: inside the error boundary the React tree is in a broken state (often a stale-chunk error right after a deploy), so a hard page load is the reliable way back home; a client-side <Link> navigation can re-throw the same error. */}
          <a
            href="/"
            className="inline-flex items-center justify-center h-12 px-8 border border-foreground/20 text-foreground text-[14px] font-medium tracking-wide hover:border-foreground/40 transition-colors rounded-lg"
            style={{ transitionDuration: "var(--duration-fast)" }}
          >
            {t("notFound.toHome")}
          </a>
        </div>
      </div>
    </div>
  );
}
